import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowLeft,
  Shield,
  Clock,
  Users,
  Globe,
  FileText,
  Eye,
  Lock,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAuth0Context } from "@/contexts/Auth0Context";
import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";

export const Privacy = () => {
  const { isAuthenticated: legacyAuth, logout: legacyLogout } = useAuth();
  const {
    isAuthenticated: auth0Authenticated,
    loginWithRedirect,
    isLoading,
    user: auth0User,
    logout: auth0Logout,
  } = useAuth0Context();

  const handleSignIn = async () => {
    try {
      await loginWithRedirect();
    } catch (error) {
      console.error("Sign in error:", error);
    }
  };

  const handleSignUp = async () => {
    try {
      // For Auth0, we can pass screen_hint=signup to show signup form
      await loginWithRedirect();
    } catch (error) {
      console.error("Sign up error:", error);
    }
  };

  // Show loading state while Auth0 is initializing
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white landing-dark">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-gray-900/80 backdrop-blur-lg border-b border-gray-700">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold gradient-text-purple">
                SnapLading
              </h1>
            </div>
            <div className="flex items-center gap-4">
              {auth0Authenticated || legacyAuth ? (
                <>
                  <Button
                    size="sm"
                    className="button-purple button-modern"
                    asChild
                  >
                    <a href="/app/upload">Dashboard</a>
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-300 hover:text-white"
                    onClick={() => {
                      if (auth0Authenticated) {
                        auth0Logout();
                      } else {
                        legacyLogout();
                      }
                    }}
                  >
                    Sign Out
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-gray-300 hover:text-white"
                    onClick={handleSignIn}
                  >
                    Sign In
                  </Button>
                  <Button
                    size="sm"
                    className="button-purple button-modern"
                    onClick={handleSignUp}
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 lg:px-8 relative bg-black">
        <div className="container mx-auto max-w-7xl">
          <div className="text-center space-y-8">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight">
              <span className="text-white">Privacy </span>
              <span className="gradient-text-purple">Policy</span>
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 max-w-3xl mx-auto">
              Your privacy and data security are our top priorities. Learn how
              we protect your information.
            </p>
          </div>
        </div>
      </section>

      {/* Privacy Policy Content */}
      <section className="py-20 px-4 lg:px-8 gradient-bg-purple-grey">
        <div className="container mx-auto max-w-4xl">
          <div className="space-y-12">
            {/* Introduction */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <FileText className="w-6 h-6 text-purple-400" />
                  Introduction
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>
                  This Privacy Policy ("Policy") governs the collection, use,
                  disclosure, and retention of personal information by Open
                  Verify Inc. ("SnapLading", "we", "us", or "our") in connection
                  with our AI-powered logistics platform and related services.
                </p>
                <p>
                  <strong>Effective Date:</strong>{" "}
                  {new Date().toLocaleDateString()}
                </p>
                <p>
                  <strong>Jurisdiction:</strong> This Policy is governed by the
                  laws of the Province of Ontario, Canada, and the federal laws
                  of Canada applicable therein.
                </p>
              </CardContent>
            </Card>

            {/* Information We Collect */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Eye className="w-6 h-6 text-purple-400" />
                  Information We Collect
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>We collect the following types of information:</p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Account Information:</strong> Name, email address,
                      company information, and authentication credentials
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Document Data:</strong> Bills of lading, shipping
                      documents, and related logistics information uploaded to
                      our platform
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Usage Data:</strong> Information about how you use
                      our services, including processing volumes, feature usage,
                      and performance metrics
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Technical Data:</strong> IP addresses, device
                      information, browser type, and other technical information
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Communication Data:</strong> Information from
                      customer support interactions, feedback, and
                      communications
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* How We Use Information */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Shield className="w-6 h-6 text-purple-400" />
                  How We Use Your Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>We use your information for the following purposes:</p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Service Provision:</strong> To provide, maintain,
                      and improve our AI-powered document processing services
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>AI Processing:</strong> To train and improve our
                      machine learning models for document intelligence and data
                      extraction
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Customer Support:</strong> To respond to your
                      inquiries, provide technical support, and resolve issues
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Billing and Payment:</strong> To process payments,
                      manage subscriptions, and handle billing inquiries
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Security:</strong> To protect against fraud,
                      abuse, and security threats
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Compliance:</strong> To comply with legal
                      obligations and regulatory requirements
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Sharing */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Users className="w-6 h-6 text-purple-400" />
                  Data Sharing and Disclosure
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>
                  We may share your information in the following circumstances:
                </p>

                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Third-Party Service Providers
                    </h4>
                    <p>
                      We may share your information with third-party service
                      providers only as required for the operations of our
                      application, including:
                    </p>
                    <ul className="space-y-1 ml-6 mt-2">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Cloud infrastructure providers (Microsoft Azure, AWS,
                          Google Cloud)
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Payment processors and billing services</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Customer support and communication tools</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Analytics and monitoring services</span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Partner Data Sharing
                    </h4>
                    <p>
                      We may share non-personalized, scrubbed data with partners
                      to support product development and improvement. This data:
                    </p>
                    <ul className="space-y-1 ml-6 mt-2">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Is completely anonymized and de-identified</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Contains no personal or company-identifying
                          information
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>
                          Is used solely for improving AI models and service
                          quality
                        </span>
                      </li>
                    </ul>
                  </div>

                  <div>
                    <h4 className="font-semibold text-white mb-2">
                      Legal Requirements
                    </h4>
                    <p>
                      We may disclose your information when required by law,
                      regulation, or legal process, including:
                    </p>
                    <ul className="space-y-1 ml-6 mt-2">
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Compliance with court orders or subpoenas</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Regulatory investigations or audits</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-purple-400">•</span>
                        <span>Protection of rights, property, or safety</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Data Retention */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Clock className="w-6 h-6 text-purple-400" />
                  Data Retention and Deletion
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-purple-900/20 border border-purple-600/30 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    90-Day Deletion Policy
                  </h4>
                  <p>
                    Once data is deleted by the customer, it is permanently
                    deleted after 90 days. This includes all associated backups,
                    logs, and derived data.
                  </p>
                </div>

                <p>Our data retention practices are as follows:</p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Active Data:</strong> Retained while your account
                      is active and you continue to use our services
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Deletion Request:</strong> When you request
                      deletion, data is marked for deletion and removed within
                      90 days
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Account Termination:</strong> Upon account
                      termination, all associated data is deleted within 90 days
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Legal Requirements:</strong> Some data may be
                      retained longer if required by law or for legitimate
                      business purposes
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Data Security */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Lock className="w-6 h-6 text-purple-400" />
                  Data Security
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>
                  We implement comprehensive security measures to protect your
                  data:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Encryption:</strong> All data is encrypted in
                      transit and at rest using industry-standard encryption
                      protocols
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Access Controls:</strong> Strict access controls
                      and authentication mechanisms protect against unauthorized
                      access
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Security Monitoring:</strong> Continuous
                      monitoring and threat detection systems
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Regular Audits:</strong> Periodic security
                      assessments and penetration testing
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Employee Training:</strong> Regular security
                      training for all employees
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Shield className="w-6 h-6 text-purple-400" />
                  Your Rights and Choices
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>
                  Depending on your jurisdiction and applicable law, you may
                  have some or all of the following rights regarding your
                  personal information:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Access:</strong> Request access to your personal
                      information
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Correction:</strong> Request correction of
                      inaccurate or incomplete information
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Deletion:</strong> Request deletion of your
                      personal information (subject to 90-day retention period
                      and any applicable legal exceptions)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Portability:</strong> Request a copy of your data
                      in a portable format
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Objection:</strong> Object to certain processing
                      activities
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Withdrawal:</strong> Withdraw consent for
                      processing based on consent, where applicable
                    </span>
                  </li>
                </ul>
                <p className="mt-4">
                  To exercise rights available to you under your local laws,
                  please contact us at{" "}
                  <a
                    href="mailto:privacy@snaplading.com"
                    className="text-purple-400 hover:text-purple-300"
                  >
                    privacy@snaplading.com
                  </a>
                </p>
              </CardContent>
            </Card>

            {/* Jurisdiction and Governing Law */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <Globe className="w-6 h-6 text-purple-400" />
                  Jurisdiction and Governing Law
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <div className="bg-purple-900/20 border border-purple-600/30 rounded-lg p-4">
                  <h4 className="font-semibold text-white mb-2">
                    Ontario, Canada Jurisdiction
                  </h4>
                  <p>
                    This Privacy Policy is governed by and construed in
                    accordance with the laws of the Province of Ontario, Canada,
                    and the federal laws of Canada applicable therein.
                  </p>
                </div>

                <p>Key jurisdictional provisions:</p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Governing Law:</strong> Ontario, Canada law
                      governs this Policy and any disputes arising from it
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Dispute Resolution:</strong> Any disputes shall be
                      resolved in the courts of Ontario, Canada
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>Compliance:</strong> We comply with applicable
                      Canadian privacy laws, including PIPEDA
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      <strong>International Transfers:</strong> Data may be
                      transferred internationally in compliance with applicable
                      laws
                    </span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <FileText className="w-6 h-6 text-purple-400" />
                  Contact Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>
                  If you have any questions about this Privacy Policy or our
                  data practices, please contact us:
                </p>
                <div className="space-y-2">
                  <p>
                    <strong>Email:</strong>{" "}
                    <a
                      href="mailto:privacy@snaplading.com"
                      className="text-purple-400 hover:text-purple-300"
                    >
                      privacy@snaplading.com
                    </a>
                  </p>
                  <p>
                    <strong>Address:</strong> Open Verify Inc., Ontario, Canada
                  </p>
                </div>
                <p className="mt-4">
                  We will respond to your inquiry within 30 days of receipt.
                </p>
              </CardContent>
            </Card>

            {/* Updates to Policy */}
            <Card className="bg-gray-900 border-gray-800">
              <CardHeader>
                <CardTitle className="text-2xl text-white flex items-center gap-3">
                  <FileText className="w-6 h-6 text-purple-400" />
                  Updates to This Policy
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 text-gray-300">
                <p>
                  We may update this Privacy Policy from time to time to reflect
                  changes in our practices or applicable laws. We will notify
                  you of any material changes by:
                </p>
                <ul className="space-y-2 ml-6">
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>Posting the updated Policy on our website</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>Sending email notifications to registered users</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-purple-400">•</span>
                    <span>
                      Updating the "Effective Date" at the top of this Policy
                    </span>
                  </li>
                </ul>
                <p className="mt-4">
                  Your continued use of our services after any changes
                  constitutes acceptance of the updated Policy.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 lg:px-8 gradient-bg-purple-grey">
        <div className="container mx-auto max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="col-span-2 md:col-span-1">
              <h3 className="text-2xl font-bold mb-4 gradient-text-purple">
                SnapLading
              </h3>
              <p className="text-sm text-white">
                AI agent bill of lading processing for modern logistics.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-white">Product</h4>
              <ul className="space-y-2 text-sm text-white">
                <li>
                  <a
                    href="/docs"
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Documentation
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-white">Company</h4>
              <ul className="space-y-2 text-sm text-white">
                <li>
                  <a
                    href="/careers"
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Careers
                  </a>
                </li>
                <li>
                  <a
                    href="/contact"
                    className="text-white hover:text-purple-400 transition-colors"
                  >
                    Contact
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3 text-white">Legal</h4>
              <ul className="space-y-2 text-sm text-black">
                <li>
                  <a
                    href="/privacy"
                    className="text-black hover:text-purple-400 transition-colors"
                  >
                    Privacy
                  </a>
                </li>
                <li>
                  <a
                    href="/terms"
                    className="text-black hover:text-purple-400 transition-colors"
                  >
                    Terms
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-black">
              © {new Date().getFullYear()} Open Verify Inc. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};
